import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const styles = {
    carouselDot: {
        height: '8px',
        width: '8px',
        backgroundColor: '#E5E7EB',
        borderRadius: '50%',
        display: 'inline-block',
        margin: '0 4px',
        transition: 'background-color 0.3s',
    },
    carouselDotActive: {
        backgroundColor: '#FFFFFF',
    },
    navButton: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        color: '#FFFFFF',
        padding: '10px',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        zIndex: 10,
    },
    navButtonHover: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
};

const PackageCard = ({ pkg, contactNumbers }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);

    const handleNextImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === pkg.images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const handlePrevImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? pkg.images.length - 1 : prevIndex - 1
        );
    };

    // **Auto-shift images every 3 seconds**
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === pkg.images.length - 1 ? 0 : prevIndex + 1
            );
        }, 4000); // 3 seconds

        return () => clearInterval(interval);
    }, [pkg.images.length]);

    const formatDaysNights = (str) => {
        const match = str.match(/(\d+)D\/(\d+)N/);
        if (!match) return str;
        const [, days, nights] = match;
        return `${parseInt(days)} days & ${parseInt(nights)} nights`;
    };

    const phoneNumber = contactNumbers?.phone || '+916360204100';
    const whatsappNumber = contactNumbers?.whatsapp || '+916360204100';

    return (
        <Link to={`/package-detail/${pkg._id}`} className="block">
            <div
                className="relative m-2 flex-shrink-0 w-64 rounded-lg overflow-hidden shadow-md"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <div className="relative overflow-hidden h-48 w-full">
                    {/* Image slider */}
                    <div
                        className="flex h-48 transition-transform duration-500 ease-in-out"
                        style={{
                            transform: `translateX(-${currentImageIndex * (100 / pkg.images.length)}%)`,
                            width: `${pkg.images.length * 100}%`,
                        }}
                    >
                        {pkg.images.map((imgSrc, index) => (
                            <img
                                key={index}
                                src={imgSrc}
                                alt={pkg.title}
                                loading="lazy"
                                className="h-48 object-cover flex-shrink-0"
                                style={{ width: `${100 / pkg.images.length}%` }}
                            />
                        ))}
                    </div>

                    {/* Navigation buttons */}
                    {isHovering && (
                        <>
                            <button
                                style={{ ...styles.navButton, left: '10px' }}
                                onMouseEnter={(e) =>
                                    e.currentTarget.style.backgroundColor =
                                    styles.navButtonHover.backgroundColor
                                }
                                onMouseLeave={(e) =>
                                    e.currentTarget.style.backgroundColor =
                                    styles.navButton.backgroundColor
                                }
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePrevImage();
                                }}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                style={{ ...styles.navButton, right: '10px' }}
                                onMouseEnter={(e) =>
                                    e.currentTarget.style.backgroundColor =
                                    styles.navButtonHover.backgroundColor
                                }
                                onMouseLeave={(e) =>
                                    e.currentTarget.style.backgroundColor =
                                    styles.navButton.backgroundColor
                                }
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNextImage();
                                }}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </>
                    )}

                    {/* Dots */}
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                        {pkg.images.map((_, index) => (
                            <span
                                key={index}
                                style={{
                                    ...styles.carouselDot,
                                    ...(index === currentImageIndex ? styles.carouselDotActive : {}),
                                }}
                            ></span>
                        ))}
                    </div>
                </div>

                {/* Details */}
                <div className="p-4 bg-custom-white">
                    <p className="text-sm text-gray-600">{formatDaysNights(pkg.days)}</p>
                    <div className="flex items-center mt-1">
                        <svg className="w-4 h-4 text-custom-yellow mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-sm text-gray-600">{pkg.rating} ({pkg.reviews})</span>
                    </div>
                    <h3 className="mt-2 text-sm font-semibold text-custom-blue line-clamp-2 h-10">{pkg.title}</h3>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">{pkg.itinerary1}</p>
                    <div className="flex justify-between items-center mt-2">
                        <div>
                            <span className="text-sm font-bold text-custom-blue">₹ {pkg.price.toLocaleString()}</span>
                            <span className="text-xs text-gray-500 line-through ml-2">₹ {pkg.originalPrice.toLocaleString()}</span>
                            <br />
                            <span className="text-xs text-custom-yellow ">SAVE ₹ {(pkg.originalPrice - pkg.price).toLocaleString()}</span>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-4 gap-3 mt-3">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                window.open(`tel:${phoneNumber}`);
                            }}
                            className="w-full border-custom-blue border-2 text-custom-blue py-2 rounded-lg flex items-center justify-center hover:bg-custom-blue hover:text-white"
                        >
                            <img src="/calllogo.svg" alt="Call" className="w-4 h-4" />
                        </button>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                const detailLink = `${process.env.REACT_APP_BASE_URL}/package-detail/${pkg._id}`;
                                const message = `I'm interested in the tour package "${pkg.title}". More details: ${detailLink}`;
                                const encodedMessage = encodeURIComponent(message);
                                const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
                                window.open(whatsappUrl);
                            }}
                            className="md:col-span-3 w-full bg-custom-yellow text-custom-blue py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-custom-blue hover:text-white group"
                        >
                            <img
                                src="/whatsapp-blue.svg"
                                alt="WhatsApp"
                                className="w-5 h-5 transition-colors duration-200 group-hover:filter group-hover:invert group-hover:brightness-0"
                            />
                            WhatsApp
                        </button>
                    </div>
                </div>
            </div>
        </Link>
    );
};

export default PackageCard;
